import { useState, useMemo, useEffect } from "react";
import {
  Columns, FileXls, FilePdf, FileCsv, Copy, Trophy, Medal, Flag, SealCheck, FileCode,
  CaretUp, CaretDown, LinkSimple, MagnifyingGlass, Warning, ArrowCounterClockwise, ArrowClockwise, XCircle,
  Prohibit, ClipboardText, FloppyDisk, X, TextAlignLeft,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { api, downloadBlob } from "../lib/api";
import { fileColor, verifyColor } from "../lib/files";
import { ColumnFilter } from "./ColumnFilter";
import { FORMULA_MODE_EVT, FORMULA_COL_EVT } from "./FormulaBuilder";

const RATING_META = {
  gold: { label: "Gold", cls: "badge-gold", Icon: Trophy },
  silver: { label: "Silver", cls: "badge-silver", Icon: Medal },
  bronze: { label: "Bronze", cls: "badge-bronze", Icon: Medal },
  red_flag: { label: "Incerto", cls: "badge-red_flag", Icon: Warning },
  manual: { label: "Manuale", cls: "badge-manual", Icon: LinkSimple },
  unmatched: { label: "Non abbinato", cls: "badge-unmatched", Icon: XCircle },
  ignored: { label: "Ignorata", cls: "badge-ignored", Icon: Prohibit },
  subtotal: { label: "Totale", cls: "badge-manual", Icon: ClipboardText },
};
const RATING_ORDER = ["gold", "silver", "bronze", "red_flag", "manual", "unmatched", "ignored"];

function RatingBadge({ value }) {
  if (!value) return <span className="text-muted-foreground/40">—</span>;
  const m = RATING_META[value] || RATING_META.unmatched;
  const { Icon } = m;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${m.cls}`}>
      <Icon size={13} weight="fill" /> {m.label}
    </span>
  );
}

const CHIP_COLORS = {
  all: "hsl(230,60%,35%)", gold: "hsl(41,88%,40%)", silver: "hsl(220,9%,42%)",
  bronze: "hsl(25,65%,40%)", red_flag: "hsl(0,72%,48%)", unmatched: "hsl(320,75%,45%)",
  manual: "hsl(201,88%,37%)", ignored: "hsl(215,12%,45%)",
};

function FilterChip({ label, value, active, color, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-2 rounded-lg border flex items-center gap-2 text-sm transition-colors ${active ? "ring-2 ring-offset-1 ring-[hsl(var(--accent))]" : ""}`}
      style={{ background: `${color}1f`, borderColor: `${color}88`, color }}>
      <span className="text-xs">{label}</span>
      <span className="font-bold">{value}</span>
    </button>
  );
}

function toNum(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(/\s/g, "").replace(",", "."));
  return Number.isNaN(n) ? null : n;
}

function statsOf(rows) {
  const s = { gold: 0, silver: 0, bronze: 0, red_flag: 0, manual: 0, unmatched: 0, ignored: 0, total: 0 };
  for (const r of rows) {
    const rt = r._rating;
    if (rt === "subtotal") continue;
    s.total++;
    if (rt === "ignored") s.ignored++;
    else if (rt === "manual") s.manual++;
    else if (r._matched === false || rt === "unmatched") s.unmatched++;
    else if (rt in s) s[rt]++;
  }
  return s;
}

const RATING_W = 210, VERIFY_W = 150;
const isDefaultVisible = (c) => !c.technical || c.verify;

export function ResultsGrid({ job, files, data, setData, onActiveChange }) {
  const columns = data.columns || [];
  const rows = data.results || [];
  const labelOf = (k) => columns.find((c) => c.key === k)?.label || k;
  const targetFileNums = [...new Set(columns.filter((c) => c.file >= 2).map((c) => c.file))];
  const rowHasFile = (r, i) => Object.keys(r).some((k) => k.startsWith(`F${i}_`) && r[k] !== null && r[k] !== "" && r[k] !== undefined);
  const hasOrphans = (i) => rows.some((o) => o._origin === `F${i}` && (o._matched === false || o._rating === "unmatched"));
  const canManual = (r) => !r._subtotal && r._rating !== "ignored" && ((r._matched === false || r._rating === "unmatched") || (r._origin === "F1" && targetFileNums.some((i) => !rowHasFile(r, i) && hasOrphans(i))));
  const verifications = data.verifications || [];
  const [visible, setVisible] = useState(() => columns.filter(isDefaultVisible).map((c) => c.key));
  const [ratingSel, setRatingSel] = useState(() => new Set());
  const [diffKey, setDiffKey] = useState(null);
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [matchRow, setMatchRow] = useState(null);
  const [showSummary, setShowSummary] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [wrap, setWrap] = useState(() => localStorage.getItem("rowtool_wrap") === "1");
  const toggleWrap = () => setWrap((w) => { localStorage.setItem("rowtool_wrap", w ? "0" : "1"); return !w; });
  const [formulaMode, setFormulaMode] = useState(false);
  useEffect(() => {
    const h = (e) => setFormulaMode(!!e.detail);
    window.addEventListener(FORMULA_MODE_EVT, h);
    return () => window.removeEventListener(FORMULA_MODE_EVT, h);
  }, []);
  const [selected, setSelected] = useState(() => new Set());
  const [lastIdx, setLastIdx] = useState(null);
  const jobFiltersKey = JSON.stringify(job.column_filters || {});
  const [colFilters, setColFilters] = useState(() => job.column_filters || {});
  useEffect(() => { setColFilters(job.column_filters || {}); }, [job.id, jobFiltersKey]);
  const setColFilter = (key, vals) => setColFilters((p) => { const n = { ...p }; if (!vals || !vals.length) delete n[key]; else n[key] = vals; return n; });
  const filtersKey = JSON.stringify(colFilters);
  useEffect(() => {
    if (!job?.id) return;
    if (filtersKey === jobFiltersKey) return;
    const t = setTimeout(() => { api.updateJob(job.id, { column_filters: colFilters }).catch(() => {}); }, 600);
    return () => clearTimeout(t);
  }, [filtersKey, job.id]);
  const [colWidths, setColWidths] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`rowtool_colw_${job.id}`)) || {}; } catch (e) { return {}; }
  });

  const colKeys = columns.map((c) => c.key).join("|");
  useEffect(() => {
    const map = Object.fromEntries(columns.map((c) => [c.key, c]));
    const keys = columns.map((c) => c.key);
    const saved = (job.output_columns || []).filter((k) => keys.includes(k));
    setVisible((prev) => {
      const base = prev.length ? prev : (saved.length ? saved : columns.filter(isDefaultVisible).map((c) => c.key));
      const kept = base.filter((k) => keys.includes(k));
      const added = keys.filter((k) => !kept.includes(k) && map[k] && (map[k].verify || (isDefaultVisible(map[k]) && !saved.length)));
      return [...kept, ...added];
    });
  }, [colKeys]);

  const fileName = (n) => files?.find((f) => f.slot === n)?.name || `File ${n}`;

  const shownCols = useMemo(() => {
    const vis = columns.filter((c) => visible.includes(c.key) && c.key !== "_origin");
    return [...vis.filter((c) => c.verify), ...vis.filter((c) => !c.verify)];
  }, [columns, visible]);
  const verifyCols = shownCols.filter((c) => c.verify);

  const toggleCol = (key) =>
    setVisible((v) => (v.includes(key) ? v.filter((k) => k !== key) : [...v, key]));

  const isDiff = (c, v) => {
    if (v === null || v === undefined || v === "" || v === "-") return false;
    if (c.format?.type === "ok_bad") return String(v) !== c.format.ok;
    const vt = c.vtype || "num";
    if (vt === "num") { const n = toNum(v); return n !== null && n !== 0; }
    return !String(v).startsWith("OK");
  };

  const ratingMatch = (r, k) => {
    if (k === "unmatched") return (r._matched === false || r._rating === "unmatched") && r._rating !== "ignored";
    if (k === "red_flag") return r._rating === "red_flag" && r._matched !== false;
    if (k === "ignored") return r._rating === "ignored";
    return r._rating === k;
  };

  const filtered = useMemo(() => {
    let out = rows;
    if (diffKey) {
      const col = columns.find((c) => c.key === diffKey);
      out = rows.filter((r) => col && isDiff(col, r[diffKey]));
    } else if (ratingSel.size) {
      out = rows.filter((r) => [...ratingSel].some((k) => ratingMatch(r, k)));
    } else {
      out = rows.filter((r) => r._rating !== "ignored");
    }
    const fentries = Object.entries(colFilters).filter(([, v]) => Array.isArray(v) && v.length);
    if (fentries.length) {
      out = out.filter((r) => fentries.every(([k, vals]) => vals.includes(r[k] === null || r[k] === undefined ? "" : String(r[k]))));
    }
    if (sort.key) {
      out = [...out].sort((a, b) => {
        const an = toNum(a[sort.key]), bn = toNum(b[sort.key]);
        let cmp;
        if (an !== null && bn !== null) cmp = an - bn;
        else cmp = String(a[sort.key] ?? "").localeCompare(String(b[sort.key] ?? ""), "it", { numeric: true });
        return sort.dir === "asc" ? cmp : -cmp;
      });
    }
    return out;
  }, [rows, ratingSel, diffKey, sort, columns, filtersKey]);

  const dataIds = (list) => list.filter((r) => !r._subtotal).map((r) => r._id);
  const activeKey = selected.size ? [...selected].sort().join(",") : dataIds(filtered).join(",");
  useEffect(() => {
    if (!onActiveChange) return;
    onActiveChange(selected.size ? [...selected] : dataIds(filtered));
  }, [activeKey]);

  const verifyStats = useMemo(() => {
    const s = {};
    verifyCols.forEach((c) => {
      let maxAbs = 0, numeric = true, any = false;
      filtered.forEach((r) => {
        const v = r[c.key];
        if (v === null || v === undefined || v === "") return;
        any = true;
        const n = toNum(v);
        if (n === null) numeric = false; else maxAbs = Math.max(maxAbs, Math.abs(n));
      });
      s[c.key] = { numeric: (c.vtype ? c.vtype === "num" : numeric) && any, maxAbs };
    });
    return s;
  }, [verifyCols, filtered]);

  const isDiffCell = (c, v) => {
    if (v === null || v === undefined || v === "") return false;
    if (c.format?.type === "ok_bad") return String(v) !== c.format.ok;
    const st = verifyStats[c.key];
    if (st?.numeric) { const n = toNum(v); return n !== null && n !== 0; }
    return !String(v).startsWith("OK");
  };

  const verifyCellStyle = (c, v) => {
    if (!c.verify || v === null || v === undefined || v === "") return {};
    const green = { background: "hsl(142,65%,90%)", color: "hsl(142,55%,26%)", fontWeight: 700 };
    const red = { background: "hsl(0,75%,91%)", color: "hsl(0,68%,40%)", fontWeight: 700 };
    const orange = { background: "hsl(38,92%,88%)", color: "hsl(30,80%,32%)", fontWeight: 700 };
    if (c.format?.type === "ok_bad") return String(v) === c.format.ok ? green : red;
    if (c.format?.type === "threshold") {
      const n = toNum(v); if (n === null) return {};
      const th = Number(c.format.threshold ?? 0), op = c.format.operator || "gt";
      const hit = (op === "gt" && n > th) || (op === "lt" && n < th) || (op === "eq" && n === th);
      return hit ? red : green;
    }
    const st = verifyStats[c.key];
    if (st?.numeric) {
      const n = toNum(v);
      if (n === null) return {};
      if (n === 0) return green;
      const ratio = st.maxAbs ? Math.abs(n) / st.maxAbs : 1;
      if (ratio <= 0.5) return orange;
      return red;
    }
    return String(v).startsWith("OK") ? green : red;
  };

  // source-cell highlighting: keys of file columns whose compared verify differs
  const rowHighlight = (r) => {
    const set = new Set();
    verifyCols.forEach((c) => {
      if (c.sources && isDiff(c, r[c.key])) c.sources.forEach((s) => set.add(s));
    });
    return set;
  };

  const ratingW = colWidths["_rating"] || RATING_W;
  const stickyLeft = (c) => {
    if (!c.verify) return null;
    return ratingW + verifyCols.indexOf(c) * VERIFY_W;
  };

  const clickHeader = (key) => {
    if (formulaMode) { window.dispatchEvent(new CustomEvent(FORMULA_COL_EVT, { detail: key })); return; }
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  };

  const doExport = async (format, extra = {}) => {
    try {
      const cols = shownCols.map((c) => ({ key: c.key, label: c.label, verify: !!c.verify, vtype: c.vtype || "num", format: c.format || null }));
      const rws = filtered.map((r) => Object.fromEntries(shownCols.map((c) => [c.key, r[c.key]])));
      const blob = await api.export(job.id, format, cols, rws, extra);
      downloadBlob(blob, `${job.name}.${format === "excel" ? "xlsx" : format}`);
      toast.success(`Export ${format.toUpperCase()} pronto`);
    } catch (e) { toast.error("Errore export"); }
  };

  const copyData = async () => {
    const header = shownCols.map((c) => c.label).join("\t");
    const body = filtered.map((r) => shownCols.map((c) => r[c.key] ?? "").join("\t")).join("\n");
    await navigator.clipboard.writeText(`${header}\n${body}`);
    toast.success("Dati copiati negli appunti");
  };

  const doUndo = async () => {
    try {
      const res = await api.undo(job.id);
      setData({ ...data, results: res.results, columns: res.columns, stats: res.stats, verifications: res.verifications || data.verifications });
      toast.success("Ultima azione annullata");
    } catch (e) { toast.message(e.response?.data?.detail || "Niente da annullare"); }
  };

  const doRedo = async () => {
    try {
      const res = await api.redo(job.id);
      setData({ ...data, results: res.results, columns: res.columns, stats: res.stats, verifications: res.verifications || data.verifications });
      toast.success("Azione ripetuta");
    } catch (e) { toast.message(e.response?.data?.detail || "Niente da ripetere"); }
  };

  const startResize = (e, key) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    const startW = colWidths[key] || (key === "_rating" ? RATING_W : 140);
    const onMove = (ev) => setColWidths((p) => ({ ...p, [key]: Math.max(60, startW + ev.clientX - startX) }));
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setColWidths((p) => { try { localStorage.setItem(`rowtool_colw_${job.id}`, JSON.stringify(p)); } catch (er) {} return p; });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const changeRating = async (row, rating) => {
    try {
      const res = await api.setRating(job.id, row._id, rating);
      setData({ ...data, results: rows.map((r) => (r._id === row._id ? { ...r, _rating: res.rating, _matched: res.matched } : r)), stats: res.stats });
    } catch (e) { toast.error("Errore aggiornamento rating"); }
  };

  const toggleSelect = (idx, row, e) => {
    if (e.shiftKey && lastIdx !== null) {
      const [a, b] = [Math.min(lastIdx, idx), Math.max(lastIdx, idx)];
      const ids = filtered.slice(a, b + 1).map((r) => r._id);
      setSelected((prev) => { const n = new Set(prev); ids.forEach((i) => n.add(i)); return n; });
      setLastIdx(idx);
    } else {
      setSelected((prev) => { const n = new Set(prev); if (n.has(row._id)) n.delete(row._id); else n.add(row._id); return n; });
      setLastIdx(idx);
    }
  };

  const bulkRating = async (rating) => {
    try {
      const res = await api.setRatingBulk(job.id, [...selected], rating);
      const idset = new Set(res.row_ids);
      setData({ ...data, results: rows.map((r) => (idset.has(r._id) ? { ...r, _rating: res.rating, _matched: res.matched } : r)), stats: res.stats });
      setSelected(new Set());
      toast.success(`${res.updated} righe aggiornate`);
    } catch (e) { toast.error("Errore aggiornamento massivo"); }
  };

  const toggleOne = (row) =>
    setSelected((prev) => { const n = new Set(prev); if (n.has(row._id)) n.delete(row._id); else n.add(row._id); return n; });
  const allVisibleSelected = filtered.length > 0 && filtered.every((r) => selected.has(r._id));
  const toggleSelectAll = () =>
    setSelected(allVisibleSelected ? new Set() : new Set(filtered.map((r) => r._id)));

  useEffect(() => {
    if (!job?.id || !visible.length) return;
    const t = setTimeout(() => { api.updateJob(job.id, { output_columns: visible }).catch(() => {}); }, 600);
    return () => clearTimeout(t);
  }, [visible.join("|")]);

  const ratingBase = useMemo(() => {
    if (diffKey) { const col = columns.find((c) => c.key === diffKey); return rows.filter((r) => col && isDiff(col, r[diffKey])); }
    if (ratingSel.size) return rows.filter((r) => [...ratingSel].some((k) => ratingMatch(r, k)));
    return rows.filter((r) => r._rating !== "ignored");
  }, [rows, ratingSel, diffKey, columns]);

  const colFilterActive = Object.values(colFilters).some((v) => Array.isArray(v) && v.length);
  const columnFilteredAll = useMemo(() => {
    const fentries = Object.entries(colFilters).filter(([, v]) => Array.isArray(v) && v.length);
    if (!fentries.length) return rows;
    return rows.filter((r) => fentries.every(([k, vals]) => vals.includes(r[k] === null || r[k] === undefined ? "" : String(r[k]))));
  }, [rows, filtersKey]);
  const chipStats = useMemo(() => (colFilterActive ? statsOf(columnFilteredAll) : (data.stats || {})), [colFilterActive, columnFilteredAll, data.stats]);

  const stats = chipStats;
  const dataCols = columns.filter((c) => c.key !== "_origin");
  const toggleRating = (k) => { setDiffKey(null); setRatingSel((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; }); };
  const clearFilter = () => { setRatingSel(new Set()); setDiffKey(null); };
  const noFilter = ratingSel.size === 0 && !diffKey;

  const renderCell = (c, r) => {
    if (c.key === "_rating" || c.key.startsWith("_rating_f")) return <RatingBadge value={r[c.key]} />;
    const v = r[c.key];
    if (v === null || v === undefined || v === "") return <span className="text-muted-foreground/40">—</span>;
    return String(v);
  };

  return (
    <section data-testid="results-section">
      <div className="flex items-center gap-3 mb-4">
        <span className="stage-num">3</span>
        <h2 className="text-xl font-head font-bold">Risultato abbinato</h2>
        <SealCheck size={18} className="text-[hsl(var(--neon))]" weight="duotone" />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <FilterChip label="tutti" value={(stats.total ?? rows.length) - (stats.ignored ?? 0)} color={CHIP_COLORS.all} active={noFilter} onClick={clearFilter} testid="filter-all" />
        <FilterChip label="gold" value={stats.gold ?? 0} color={CHIP_COLORS.gold} active={ratingSel.has("gold")} onClick={() => toggleRating("gold")} testid="filter-gold" />
        <FilterChip label="silver" value={stats.silver ?? 0} color={CHIP_COLORS.silver} active={ratingSel.has("silver")} onClick={() => toggleRating("silver")} testid="filter-silver" />
        <FilterChip label="bronze" value={stats.bronze ?? 0} color={CHIP_COLORS.bronze} active={ratingSel.has("bronze")} onClick={() => toggleRating("bronze")} testid="filter-bronze" />
        <FilterChip label="incerto" value={stats.red_flag ?? 0} color={CHIP_COLORS.red_flag} active={ratingSel.has("red_flag")} onClick={() => toggleRating("red_flag")} testid="filter-red_flag" />
        <FilterChip label="manuali" value={stats.manual ?? 0} color={CHIP_COLORS.manual} active={ratingSel.has("manual")} onClick={() => toggleRating("manual")} testid="filter-manual" />
        <FilterChip label="non abbinate" value={stats.unmatched ?? 0} color={CHIP_COLORS.unmatched} active={ratingSel.has("unmatched")} onClick={() => toggleRating("unmatched")} testid="filter-unmatched" />
        <FilterChip label="ignorate" value={stats.ignored ?? 0} color={CHIP_COLORS.ignored} active={ratingSel.has("ignored")} onClick={() => toggleRating("ignored")} testid="filter-ignored" />
      </div>

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3 p-3 rounded-xl border border-[hsl(var(--accent)/0.5)] bg-[hsl(var(--accent)/0.08)]" data-testid="bulk-bar">
          <span className="text-sm font-semibold">{selected.size} righe selezionate — cambia stato:</span>
          {RATING_ORDER.map((rt) => (
            <button key={rt} data-testid={`bulk-rating-${rt}`} onClick={() => bulkRating(rt)}>
              <RatingBadge value={rt} />
            </button>
          ))}
          <Button size="sm" variant="ghost" className="ml-auto gap-1" data-testid="bulk-clear" onClick={() => setSelected(new Set())}><X size={14} /> Deseleziona</Button>
        </div>
      )}

      {verifications.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4 mb-4" data-testid="verify-summary-panel">
          <button className="flex items-center gap-2 text-sm font-semibold" onClick={() => setShowSummary((s) => !s)}>
            <ClipboardText size={18} className="text-[hsl(var(--accent))]" /> Riepilogo verifiche ({verifications.length})
            {showSummary ? <CaretUp size={14} /> : <CaretDown size={14} />}
          </button>
          {showSummary && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-3">
              {verifications.map((v, i) => (
                <div key={i} className={`rounded-lg border border-border p-3 bg-muted/40 text-xs grid-font ${v.column ? "cursor-pointer hover:border-[hsl(var(--accent))]" : ""}`}
                  data-testid={`verify-summary-${i}`}
                  onClick={() => { if (v.column) { setRatingSel(new Set()); setDiffKey(v.column); } }}
                  title={v.column ? "Clicca per vedere solo le righe con differenze" : ""}>
                  <div className="font-semibold text-[hsl(var(--accent))] mb-1">{v.tool}</div>
                  {Object.entries(v).filter(([k]) => k !== "tool" && k !== "dettaglio" && k !== "column" && k !== "tabella").map(([k, val]) => (
                    <div key={k} className="flex justify-between gap-2"><span className="text-muted-foreground">{k}</span><span className="font-semibold text-right break-all">{Array.isArray(val) ? val.join(", ") : String(val)}</span></div>
                  ))}
                  {Array.isArray(v.tabella) && v.tabella.length > 0 && <PeriodTable rows={v.tabella} labelOf={labelOf} testid={`verify-period-table-${i}`} />}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" data-testid="column-selector-btn" className="gap-2">
              <Columns size={16} /> Colonne ({shownCols.length}/{dataCols.length})
            </Button>
          </PopoverTrigger>
          <PopoverContent className="bg-popover border-border w-72 p-0">
            <ScrollArea className="h-72 p-3">
              <div className="space-y-1.5">
                {dataCols.map((c) => (
                  <label key={c.key} className="flex items-center gap-2 text-sm py-1 cursor-pointer" data-testid={`col-toggle-${c.key}`}>
                    <Checkbox checked={visible.includes(c.key)} onCheckedChange={() => toggleCol(c.key)} />
                    <span className="grid-font text-xs" style={{ color: c.file > 0 ? fileColor(c.file) : (c.verify ? "hsl(var(--accent))" : undefined) }}>{c.label}</span>
                    {c.file > 0 && <span className="ml-auto text-[10px]" style={{ color: fileColor(c.file) }}>{fileName(c.file)}</span>}
                    {c.verify && <span className="ml-auto text-[10px] text-[hsl(var(--accent))]">verifica</span>}
                    {c.technical && !c.verify && <span className="ml-auto text-[10px] text-muted-foreground">tecnica</span>}
                  </label>
                ))}
              </div>
            </ScrollArea>
          </PopoverContent>
        </Popover>

        <Button size="sm" variant="outline" onClick={doUndo} data-testid="undo-btn" className="gap-2">
          <ArrowCounterClockwise size={16} /> Annulla
        </Button>
        <Button size="sm" variant="outline" onClick={doRedo} data-testid="redo-btn" className="gap-2">
          <ArrowClockwise size={16} /> Ripeti
        </Button>
        <Button size="sm" variant="outline" onClick={toggleWrap} data-testid="wrap-toggle-btn" className="gap-2"
          title={wrap ? "Testo a capo: clicca per tagliare" : "Testo tagliato: clicca per andare a capo"}>
          <TextAlignLeft size={16} /> {wrap ? "A capo" : "Clip"}
        </Button>
        {formulaMode && (
          <span data-testid="formula-mode-hint" className="text-xs text-[hsl(var(--accent))] px-2 py-1 rounded-md bg-[hsl(var(--accent)/0.08)] border border-[hsl(var(--accent)/0.3)]">
            Modalità formula: clicca un'intestazione per inserire la colonna
          </span>
        )}
        {colFilterActive && (
          <span data-testid="active-filter-hint" className="text-xs text-[hsl(var(--accent))] flex items-center gap-1 px-2 py-1 rounded-md bg-[hsl(var(--accent)/0.08)] border border-[hsl(var(--accent)/0.3)]">
            <MagnifyingGlass size={13} /> Filtri attivi · le funzioni usano {filtered.length} righe
          </span>
        )}

        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={copyData} data-testid="copy-btn" className="gap-2"><Copy size={16} /> Copia</Button>
          <Button size="sm" variant="outline" onClick={() => doExport("csv")} data-testid="export-csv-btn" className="gap-2"><FileCsv size={16} /> CSV</Button>
          <Button size="sm" variant="outline" onClick={() => doExport("pdf")} data-testid="export-pdf-btn" className="gap-2"><FilePdf size={16} /> PDF</Button>
          <Button size="sm" variant="outline" onClick={() => doExport("json", { include_pdf: true })} data-testid="export-json-btn" className="gap-2"><FileCode size={16} /> JSON</Button>
          <Button size="sm" onClick={() => doExport("excel")} data-testid="export-excel-btn" className="gap-2 bg-[hsl(var(--neon))] hover:bg-[hsl(142,70%,32%)] text-white"><FileXls size={16} weight="fill" /> Excel</Button>
        </div>
      </div>

      <div className="rounded-xl border border-border overflow-hidden bg-card">
        <div className="max-h-[560px] overflow-auto">
          <table className="text-sm border-collapse grid-font" data-testid="results-table" style={{ minWidth: "100%" }}>
            <thead className="sticky top-0 z-20">
              <tr className="bg-secondary">
                <th className="sticky left-0 z-40 bg-secondary text-left px-3 py-2 border border-border whitespace-nowrap text-xs"
                  style={{ width: ratingW, minWidth: ratingW }}>
                  <div className="flex items-center gap-2">
                    <Checkbox checked={allVisibleSelected} onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox" />
                    <span>Rating / azioni</span>
                  </div>
                  <div onMouseDown={(e) => startResize(e, "_rating")} data-testid="col-resize-_rating"
                    className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-[hsl(var(--accent))]" />
                </th>
                {shownCols.map((c) => {
                  const left = stickyLeft(c);
                  const w = !c.verify ? colWidths[c.key] : null;
                  const base = c.verify
                    ? { position: "sticky", left, background: "hsl(210,60%,94%)", width: VERIFY_W, minWidth: VERIFY_W, boxShadow: `inset 0 3px 0 0 ${verifyColor(c.key)}` }
                    : { position: "relative", ...(c.file > 0 ? { boxShadow: `inset 0 3px 0 0 ${fileColor(c.file)}` } : {}), ...(w ? { width: w, minWidth: w, maxWidth: w } : {}) };
                  const vTitle = c.verify ? ("Verifica: " + ((c.sources || []).map(labelOf).join(", ") || c.label)) : undefined;
                  return (
                    <th key={c.key} onClick={() => clickHeader(c.key)} data-testid={`th-${c.key}`} title={vTitle}
                      className={`sortable-th text-left px-3 py-2 border border-border whitespace-nowrap text-xs ${c.verify ? "font-bold z-40" : ""} ${formulaMode ? "outline outline-1 outline-dashed outline-[hsl(var(--accent))] cursor-copy" : ""}`}
                      style={base}>
                      <div className="flex items-center gap-1">
                        <span style={{ color: c.verify ? verifyColor(c.key) : (c.file > 0 ? fileColor(c.file) : "hsl(var(--muted-foreground))") }}>{c.label}</span>
                        {sort.key === c.key && (sort.dir === "asc" ? <CaretUp size={12} /> : <CaretDown size={12} />)}
                        {!c.verify && <ColumnFilter colKey={c.key} baseRows={ratingBase} allFilters={colFilters} selected={colFilters[c.key]} onChange={(v) => setColFilter(c.key, v)} />}
                      </div>
                      {c.file > 0 && <div className="text-[9px] font-normal" style={{ color: fileColor(c.file) }}>{fileName(c.file)}</div>}
                      {c.verify && <div className="text-[9px] font-normal" style={{ color: verifyColor(c.key) }}>verifica</div>}
                      {!c.verify && <div onMouseDown={(e) => startResize(e, c.key)} data-testid={`col-resize-${c.key}`}
                        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-[hsl(var(--accent))]" />}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {(showAll ? filtered : filtered.slice(0, 500)).map((r, i) => {
                const hl = rowHighlight(r);
                return (
                  <tr key={r._id || i}
                    onClick={(e) => { if (e.shiftKey || e.ctrlKey || e.metaKey) { e.preventDefault(); toggleSelect(i, r, e); } }}
                    className={`transition-colors ${r._subtotal ? "font-bold bg-[hsl(var(--accent)/0.10)]" : selected.has(r._id) ? "bg-[hsl(var(--accent)/0.14)]" : "hover:bg-accent/5"}`}
                    data-testid={r._subtotal ? `subtotal-row-${i}` : `result-row-${i}`}>
                    <td className="sticky left-0 z-30 bg-card px-3 py-1.5 border border-border/60 whitespace-nowrap" style={{ width: ratingW, minWidth: ratingW }}>
                      {r._subtotal ? <RatingBadge value="subtotal" /> : (
                      <div className="flex items-center gap-2">
                        <Checkbox checked={selected.has(r._id)} onCheckedChange={() => toggleOne(r)} data-testid={`row-checkbox-${i}`} />
                        <DropdownMenu>
                          <DropdownMenuTrigger data-testid={`rating-menu-${i}`} className="cursor-pointer"
                            onPointerDown={(e) => { if (e.shiftKey || e.ctrlKey || e.metaKey) { e.preventDefault(); e.stopPropagation(); toggleSelect(i, r, e); } }}>
                            <RatingBadge value={r._rating} />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-popover border-border">
                            {RATING_ORDER.map((rt) => (
                              <DropdownMenuItem key={rt} data-testid={`rating-opt-${i}-${rt}`} onClick={() => changeRating(r, rt)}>
                                <RatingBadge value={rt} />
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        {canManual(r) && (
                          <Button size="sm" variant="outline" className="h-6 gap-1 px-2 text-xs"
                            data-testid={`manual-match-btn-${i}`} onClick={() => setMatchRow(r)}>
                            <LinkSimple size={12} /> Abbina
                          </Button>
                        )}
                      </div>
                      )}
                    </td>
                    {shownCols.map((c) => {
                      const vstyle = c.verify ? verifyCellStyle(c, r[c.key]) : (hl.has(c.key) ? { background: "hsl(0,75%,93%)" } : {});
                      const w = !c.verify ? colWidths[c.key] : null;
                      const sticky = c.verify
                        ? { position: "sticky", left: stickyLeft(c), width: VERIFY_W, minWidth: VERIFY_W, zIndex: 20, background: vstyle.background || "hsl(var(--card))" }
                        : (w ? { width: w, minWidth: w, maxWidth: w, overflow: "hidden", textOverflow: "ellipsis" } : {});
                      const wrapStyle = wrap ? { whiteSpace: "normal", overflowWrap: "anywhere" } : {};
                      const cTitle = c.verify && c.sources ? c.sources.map((s) => `${labelOf(s)}: ${r[s]}`).join("  ·  ") + `  =  ${r[c.key]}` : undefined;
                      return (
                        <td key={c.key} title={cTitle} className="px-3 py-1.5 border border-border/60 text-xs data-cell" style={{ ...vstyle, ...sticky, ...wrapStyle }}>
                          {renderCell(c, r)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {filtered.length > 500 && (
        <div className="flex items-center gap-3 mt-2" data-testid="row-limit-bar">
          {!showAll ? (
            <>
              <p className="text-xs text-muted-foreground">Anteprima limitata a 500 righe su {filtered.length}. L'export contiene tutte le righe filtrate.</p>
              <Button size="sm" variant="outline" onClick={() => setShowAll(true)} data-testid="show-all-btn" className="h-7 gap-2 text-xs">
                Mostra tutte ({filtered.length})
              </Button>
            </>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">Mostrando tutte le {filtered.length} righe.</p>
              <Button size="sm" variant="outline" onClick={() => setShowAll(false)} data-testid="show-limited-btn" className="h-7 gap-2 text-xs">
                Torna a 500
              </Button>
            </>
          )}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-2">Suggerimento: usa la <strong>casella</strong> a inizio riga, oppure <strong>Ctrl/Cmd+click</strong> o <strong>Shift+click</strong> sulle righe o sui badge per selezionarne più; trascina il bordo destro delle intestazioni per <strong>ridimensionare le colonne</strong>.</p>

      {matchRow && (
        <ManualMatchDialog job={job} files={files} clicked={matchRow} rows={rows}
          onClose={() => setMatchRow(null)}
          onDone={(res) => { setData({ ...data, results: res.results, columns: res.columns, stats: res.stats }); setMatchRow(null); }} />
      )}
    </section>
  );
}

function PeriodTable({ rows, labelOf, testid }) {
  const keys = Object.keys(rows[0]).filter((k) => k !== "periodo");
  return (
    <div className="mt-2 max-h-56 overflow-auto border border-border rounded" data-testid={testid}>
      <table className="w-full text-[11px]">
        <thead><tr className="bg-secondary sticky top-0">
          <th className="text-left px-2 py-1">periodo</th>
          {keys.map((k) => <th key={k} className="text-right px-2 py-1">{k === "righe" ? "righe" : labelOf(k)}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border/50">
              <td className="px-2 py-0.5">{r.periodo}</td>
              {keys.map((k) => <td key={k} className="px-2 py-0.5 text-right">{typeof r[k] === "number" ? r[k].toLocaleString("it-IT") : r[k]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function loadCols(slot, allCols) {
  try {
    const s = JSON.parse(localStorage.getItem(`rowtool_mm_cols_F${slot}`));
    if (Array.isArray(s) && s.length) return s.filter((c) => allCols.includes(c));
  } catch (e) { /* ignore */ }
  return allCols.slice(0, 4);
}
function saveCols(slot, cols) { localStorage.setItem(`rowtool_mm_cols_F${slot}`, JSON.stringify(cols)); }
function loadPrev(slot, allCols) {
  try {
    const s = JSON.parse(localStorage.getItem(`rowtool_mm_prev_F${slot}`));
    if (Array.isArray(s) && s.length) return s.filter((c) => allCols.includes(c));
  } catch (e) { /* ignore */ }
  return allCols.slice(0, 8);
}
function savePrev(slot, cols) { localStorage.setItem(`rowtool_mm_prev_F${slot}`, JSON.stringify(cols)); }

function ManualMatchDialog({ job, files, clicked, rows, onClose, onDone }) {
  const isBase = clicked._origin === "F1";
  const clickedFile = isBase ? 1 : Number(clicked._origin.slice(1));
  const colsOf = (slot) => files?.find((f) => f.slot === slot)?.columns || [];
  const fileName = (n) => files?.find((f) => f.slot === n)?.name || `File ${n}`;

  // orphan target files present (for base-clicked mode)
  const allSlots = useMemo(() => (files || []).map((f) => f.slot).filter((s) => s > 1).sort(), [files]);
  const matchedSlots = useMemo(() => allSlots.filter((s) => colsOf(s).some((c) => String(clicked[`F${s}_${c}`] ?? "").trim() !== "")), [allSlots, clicked, files]);
  const availableTargets = useMemo(() => {
    const free = allSlots.filter((s) => !matchedSlots.includes(s));
    return free.length ? free : allSlots;
  }, [allSlots, matchedSlots]);

  const [targetFile, setTargetFile] = useState(isBase ? (availableTargets[0] || 2) : clickedFile);
  // candidate rows + which file's columns we search/show
  const candidateFile = isBase ? targetFile : 1;
  const candidateCols = colsOf(candidateFile);
  const [showCols, setShowCols] = useState(() => loadCols(candidateFile, candidateCols));
  useEffect(() => { setShowCols(loadCols(candidateFile, colsOf(candidateFile))); }, [candidateFile]);
  const [previewShow, setPreviewShow] = useState(() => loadPrev(clickedFile, colsOf(clickedFile)));
  const togglePrev = (c) => setPreviewShow((s) => { const next = s.includes(c) ? s.filter((x) => x !== c) : [...s, c]; savePrev(clickedFile, next); return next; });

  const [search, setSearch] = useState("");
  const [pending, setPending] = useState(null);

  const candidatePool = isBase
    ? rows.filter((r) => (r._matched === false || r._rating === "unmatched") && r._origin === `F${targetFile}` && r._rating !== "ignored" && r._id !== clicked._id)
    : rows.filter((r) => r._origin === "F1");

  const q = search.trim().toLowerCase();
  const scored = candidatePool.map((r) => {
    if (!q) return { r, score: 1 };
    let score = 0;
    candidateCols.forEach((c) => {
      const val = String(r[`F${candidateFile}_${c}`] ?? "").toLowerCase();
      if (!val) return;
      if (val === q) score += 100; else if (val.startsWith(q)) score += 60; else if (val.includes(q)) score += 40;
    });
    return { r, score };
  }).filter((x) => x.score > 0).sort((a, b) => b.score - a.score);
  const candidates = (q ? scored : scored.slice(0, 50)).map((x) => x.r);

  const toggleShow = (c) => setShowCols((s) => {
    const next = s.includes(c) ? s.filter((x) => x !== c) : [...s, c];
    saveCols(candidateFile, next);
    return next;
  });

  const doMatch = async (candidate, force = false) => {
    const base_id = isBase ? clicked._id : candidate._id;
    const target_id = isBase ? candidate._id : clicked._id;
    try {
      const res = await api.manualMatch(job.id, { base_id, target_file: targetFile, target_id, force });
      if (res.warning === "already_matched") { setPending(candidate); return; }
      toast.success("Abbinamento manuale eseguito");
      onDone(res);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore abbinamento"); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-popover border-border max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><LinkSimple size={20} /> Abbina manualmente</DialogTitle>
          <DialogDescription>
            Riga di <strong style={{ color: fileColor(clickedFile) }}>{fileName(clickedFile)}</strong>. Cerca in{" "}
            <strong style={{ color: fileColor(candidateFile) }}>{fileName(candidateFile)}</strong> (tutte le colonne) la riga da collegare.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2">
          <div className="text-xs rounded-lg p-2 border border-border bg-muted/50 grid-font flex-1" data-testid="manual-clicked-preview">
            {previewShow.map((c) => (
              <span key={c} className="mr-3"><span style={{ color: fileColor(clickedFile) }}>{c}:</span> {String(clicked[`F${clickedFile}_${c}`] ?? "—")}</span>
            ))}
            {previewShow.length === 0 && <span className="text-muted-foreground">Nessun campo selezionato — scegli con "Campi"</span>}
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="manual-preview-fields-btn" className="gap-1 shrink-0"><Columns size={14} /> Campi</Button>
            </PopoverTrigger>
            <PopoverContent className="bg-popover border-border w-56 p-0">
              <ScrollArea className="h-56"><div className="p-3 space-y-1.5">
                {colsOf(clickedFile).map((c) => (
                  <label key={c} className="flex items-center gap-2 text-xs py-1 cursor-pointer" data-testid={`manual-preview-toggle-${c}`}>
                    <Checkbox checked={previewShow.includes(c)} onCheckedChange={() => togglePrev(c)} />
                    <span style={{ color: fileColor(clickedFile) }}>{c}</span>
                  </label>
                ))}
              </div></ScrollArea>
            </PopoverContent>
          </Popover>
        </div>

        {isBase && allSlots.length >= 1 && (
          <div className="flex items-center gap-2 text-xs flex-wrap">
            <span className="text-muted-foreground">Cerca in:</span>
            {allSlots.map((n) => {
              const done = matchedSlots.includes(n);
              return (
                <button key={n} data-testid={`manual-targetfile-${n}`} onClick={() => setTargetFile(n)}
                  title={done ? "Riga già abbinata a questa fonte" : "Fonte non ancora abbinata"}
                  className={`px-2 py-1 rounded border flex items-center gap-1 ${targetFile === n ? "text-white" : ""} ${done && targetFile !== n ? "opacity-70 border-dashed" : ""}`}
                  style={targetFile === n ? { background: fileColor(n), borderColor: fileColor(n) } : { borderColor: "hsl(var(--border))", color: fileColor(n) }}>
                  {done && <span>✓</span>}{fileName(n)}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-2 top-2.5 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca in tutte le colonne..."
              data-testid="manual-match-search" className="pl-8 bg-muted" autoFocus />
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="manual-cols-btn" className="gap-2"><Columns size={16} /> Colonne</Button>
            </PopoverTrigger>
            <PopoverContent className="bg-popover border-border w-56 p-0">
              <ScrollArea className="h-56"><div className="p-3 space-y-1.5">
                  {candidateCols.map((c) => (
                    <label key={c} className="flex items-center gap-2 text-xs py-1 cursor-pointer" data-testid={`manual-col-toggle-${c}`}>
                      <Checkbox checked={showCols.includes(c)} onCheckedChange={() => toggleShow(c)} />
                      <span style={{ color: fileColor(candidateFile) }}>{c}</span>
                    </label>
                  ))}
              </div></ScrollArea>
            </PopoverContent>
          </Popover>
        </div>

        <ScrollArea className="h-56 border border-border rounded-lg">
          <table className="w-full text-xs grid-font">
            <thead className="sticky top-0 bg-secondary">
              <tr>{showCols.map((c) => <th key={c} className="text-left px-2 py-1 border-b border-border" style={{ color: fileColor(candidateFile) }}>{c}</th>)}<th className="px-2 py-1 border-b border-border"></th></tr>
            </thead>
            <tbody>
              {candidates.map((b, i) => (
                <tr key={b._id} className="hover:bg-accent/5" data-testid={`manual-candidate-${i}`}>
                  {showCols.map((c) => <td key={c} className="px-2 py-1 border-b border-border/50 data-cell">{String(b[`F${candidateFile}_${c}`] ?? "—")}</td>)}
                  <td className="px-2 py-1 border-b border-border/50">
                    <Button size="sm" className="h-6 px-2 text-xs" data-testid={`manual-pick-${i}`} onClick={() => doMatch(b)}>Collega</Button>
                  </td>
                </tr>
              ))}
              {candidates.length === 0 && <tr><td colSpan={showCols.length + 1} className="px-2 py-4 text-center text-muted-foreground">Nessun risultato</td></tr>}
            </tbody>
          </table>
        </ScrollArea>

        {pending && (
          <div className="rounded-lg border border-[hsl(var(--gold)/0.6)] bg-[hsl(var(--gold)/0.12)] p-3 flex items-start gap-2" data-testid="already-matched-warning">
            <Warning size={20} className="text-[hsl(41,90%,40%)] mt-0.5" />
            <div className="text-sm flex-1">La riga scelta risulta <strong>già abbinata</strong>. Vuoi sovrascrivere?</div>
            <Button size="sm" variant="destructive" data-testid="force-match-btn" onClick={() => doMatch(pending, true)}>Sovrascrivi</Button>
          </div>
        )}

        <DialogFooter><Button variant="outline" onClick={onClose}>Chiudi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { RatingBadge };
